import React, {
  useMemo,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import {
  Clock,
  Image as ImageIcon,
  Video,
  ChevronDown,
  Trash2,
  Loader,
  ChevronUp,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { getPlatformIcon } from "../utils/platformIcons";
import { useAppContext } from "../context/AppContext";
import { useDraftStoreZustand } from "@/store/useDraftStore";
import API from "@/services/api";
import { notify } from "@/utils/toast";
import { DraftsSkeleton } from "@/components/Skeletons";

const DraftsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { dispatch, loadDraftsHistory, state } = useAppContext();

  const drafts = state?.draftHistory?.data || state?.draftHistory || [];
  const draftPagination = state?.draftHistory?.pagination || {
    page: 1,
    limit: 10,
    totalPages: 1,
    hasMore: false,
    totalPosts: 0,
  };

  const { saveOrUpdateDraft } = useDraftStoreZustand();

  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const [showScrollTop, setShowScrollTop] = useState(false);

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Scroll-to-top visibility
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      setShowScrollTop(container.scrollTop > 400);
    };

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    scrollContainerRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Initial load - only if no drafts exist
  useEffect(() => {
    if (!drafts.length && !state.draftHistoryLoading) {
      loadDraftsHistory(1, false);
    }
  }, [drafts.length, state.draftHistoryLoading, loadDraftsHistory]);

  const toggleGroup = (base: string) => {
    setExpandedGroups((prev) => ({ ...prev, [base]: !prev[base] }));
  };

  const extractBase = (postId?: string) => {
    if (!postId) return null;
    const parts = postId.split("-");
    if (parts.length >= 3) {
      return parts.slice(1).join("-");
    }
    return postId;
  };

  const PLATFORM_ORDER: Record<string, number> = {
    facebook: 0,
    instagram: 1,
    linkedin: 2,
    tiktok: 3,
    youtube: 4,
  };

  const groups = useMemo(() => {
    if (!drafts?.length) return [];

    const map = new Map<string, any[]>();

    for (const d of drafts) {
      const base = extractBase(d.postId) || d.id;
      if (!map.has(base)) map.set(base, []);
      map.get(base)!.push(d);
    }

    const result: { base: string; items: any[] }[] = [];

    for (const [base, items] of map.entries()) {
      items.sort(
        (a, b) =>
          (PLATFORM_ORDER[a.platform] ?? 999) - (PLATFORM_ORDER[b.platform] ?? 999)
      );
      result.push({ base, items });
    }

    // Sort groups by most recent update
    result.sort((a, b) => {
      const aDate = new Date(a.items[0]?.updatedAt || 0).getTime();
      const bDate = new Date(b.items[0]?.updatedAt || 0).getTime();
      return bDate - aDate;
    });

    return result;
  }, [drafts]);

  const renderPlatformIcon = (platform: string) => {
    const IconComponent = getPlatformIcon(platform as any);
    if (!IconComponent) {
      return (
        <div className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-600 text-white text-xs font-bold shrink-0">
          {platform?.slice(0, 2).toUpperCase()}
        </div>
      );
    }
    return (
      <div className="w-9 h-9 flex items-center justify-center rounded-full bg-white border border-gray-200 shrink-0">
        <IconComponent className="w-5 h-5" />
      </div>
    );
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  const getThumbnailUrl = (draft: any) => {
    if (draft?.mediaUrl) return draft.mediaUrl;
    if (draft?.posts?.[0]?.mediaUrl) return draft.posts[0].mediaUrl;
    if (draft?.posts?.[0]?.imageUrl) return draft.posts[0].imageUrl;
    if (draft?.mediaAssets?.[0]?.url) return draft.mediaAssets[0].url;
    if (typeof draft?.mediaAssets?.[0] === "string") return draft.mediaAssets[0];
    return null;
  };

  const getMediaType = (draft: any): "image" | "video" | "text" => {
    const url = getThumbnailUrl(draft)?.toLowerCase() || "";
    if (!url) return "text";
    if (url.match(/\.(jpg|jpeg|png|gif|webp)$/)) return "image";
    if (url.match(/\.(mp4|webm|mov|avi)$/)) return "video";
    if (url.includes("youtube.com") || url.includes("youtu.be")) return "video";
    return "image";
  };

  const renderThumbnail = (draft: any) => {
    const thumbnailUrl = getThumbnailUrl(draft);
    const type = getMediaType(draft);

    if (!thumbnailUrl) {
      if (type === "text") return null;
      const Icon = type === "video" ? Video : ImageIcon;
      return (
        <div className="w-full sm:w-32 h-44 sm:h-28 bg-gray-100 border-b sm:border-r border-gray-200 flex items-center justify-center shrink-0">
          <Icon className="w-10 h-10 text-gray-400" />
        </div>
      );
    }

    return (
      <div className="w-full sm:w-32 h-44 sm:h-28 p-1.5 bg-gray-200 border-b sm:border-r border-gray-200 relative shrink-0">
        <img
          src={thumbnailUrl}
          alt="Draft thumbnail"
          className="w-full h-full object-cover rounded"
          onError={(e) => {
            e.currentTarget.src = "";
            const parent = e.currentTarget.parentElement;
            if (parent) {
              parent.innerHTML = `
                <div class="w-full h-full bg-gray-100 flex items-center justify-center rounded">
                  <svg class="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    ${
                      type === "video"
                        ? '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"/>'
                        : '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>'
                    }
                  </svg>
                </div>
              `;
            }
          }}
        />
        {type === "video" && (
          <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
            <div className="w-12 h-12 bg-white/90 rounded-full flex items-center justify-center">
              <div className="w-0 h-0 border-l-6 border-r-0 border-t-3 border-b-3 border-transparent border-l-gray-800 ml-1.5" />
            </div>
          </div>
        )}
      </div>
    );
  };

  const deleteHistory = async (id: string) => {
    if (deletingId === id) return;
    setDeletingId(id);

    try {
      await API.deleteHistory(id);
      notify("success", "Draft deleted");
      // Reset and reload
      await loadDraftsHistory(1, false);
    } catch (err) {
      notify("error", "Failed to delete draft");
    } finally {
      setDeletingId(null);
    }
  };

  const handleEdit = (items: any[]) => {
    const postsForPreview = items.map((d) => ({
      platform: d.platform,
      caption: d.content || "",
      hashtags: d.hashtags || d.posts?.[0]?.hashtags || [],
      characterCount: (d.content || "").length,
      mediaUrl: d.mediaUrl || d.mediaAssets?.[0]?.url || d.mediaAssets?.[0] || undefined,
      imageUrl: d.imageUrl || d.mediaUrl || undefined,
      generationPrompt: d.generationPrompt || d.posts?.[0]?.generationPrompt || "",
      tiktok_title: d.title,
      tiktok_description: d.content,
      youtubeDescription: d.content,
      youtubeTitle: d.title,
      draftId: d.id,
      postHistoryId: d.postHistoryId || d.id,
      thumbnailUrl: items[0]?.thumbnailUrl ?? "",
      postId: d.postId,
    })) as any[];

    items.forEach((d) => saveOrUpdateDraft(d.platform, { ...d, postId: d.postId }));

    dispatch({ type: "SET_GENERATED_POSTS", payload: postsForPreview });
    dispatch({ type: "SET_CURRENT_DRAFT_ID", payload: items[0]?.id });
    dispatch({ type: "SET_PUBLISH_MODAL", payload: false });
    navigate("/content/preview");
  };

  if (state.draftHistoryLoading && !drafts.length) {
    return (
      <div className="p-4">
        <div className="max-w-5xl mx-auto mb-6">
          <h2 className="text-3xl font-bold text-gray-900">{t("drafts")}</h2>
        </div>
        <DraftsSkeleton />
      </div>
    );
  }

  if (!drafts.length) {
    return (
      <div className="p-4">
        <div className="max-w-5xl mx-auto mb-6">
          <h2 className="text-3xl font-bold text-gray-900">{t("drafts")}</h2>
        </div>
        <div className="text-center py-16">
          <Clock className="w-16 h-16 mx-auto text-purple-500 mb-4" />
          <h3 className="text-xl font-semibold text-gray-800 mb-2">
            {t("no_posts_yet")}
          </h3>
          <p className="text-gray-600">Generate an AI post to save it as a draft</p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={scrollContainerRef}
      id="draftsScrollContainer"
      className="h-[calc(100vh-120px)] overflow-y-auto relative scroll-smooth p-4"
    >
      <div className="max-w-5xl mx-auto mb-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <h2 className="text-3xl font-bold text-gray-900">{t("drafts")}</h2>
          {draftPagination.hasMore && (
            <span className="text-sm text-gray-500">
              {drafts.length} / {draftPagination.totalPosts || drafts.length}
            </span>
          )}
        </div>
      </div>

      <div className="max-w-5xl mx-auto pb-12">
        <InfiniteScroll
          dataLength={drafts.length}
          next={() => loadDraftsHistory(draftPagination.page + 1, true)}
          hasMore={draftPagination.hasMore}
          loader={<div className="mt-2">
            <DraftsSkeleton />
            </div>
          }
          scrollableTarget="draftsScrollContainer"
          endMessage={
            drafts.length > 0 && (
              <p className="text-center text-gray-400 text-sm py-8">
                {t("no_more_drafts")}
              </p>
            )
          }
        >
          <div className="space-y-6">
            {groups.map(({ base, items }) => {
              const isExpanded = !!expandedGroups[base];

              return (
                <div
                  key={base}
                  className="bg-gray-50 border border-gray-200 rounded-lg p-3 sm:p-4"
                >
                  <div className="flex flex-col gap-3">
                    <div className="flex items-start justify-between gap-3">
                      <button
                        type="button"
                        onClick={() => toggleGroup(base)}
                        className="flex-1 min-w-0 text-left"
                      >
                        <div className="flex flex-col gap-2">
                          <div className="flex items-center gap-2 text-xs text-gray-500 font-medium">
                            <Clock className="w-4 h-4 text-gray-400 shrink-0" />
                            <span className="truncate">
                              {format(new Date(items[0]?.updatedAt), "PPpp")}
                            </span>
                          </div>

                          {!isExpanded && (
                            <div className="flex flex-wrap gap-2">
                              {items.map((draft) => (
                                <div
                                  key={draft.id}
                                  title={draft.platform}
                                  className="shrink-0"
                                >
                                  {renderPlatformIcon(draft.platform)}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </button>

                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(items);
                          }}
                          className="px-4 py-1 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition"
                        >
                          {t("edit")}
                        </button>

                        <button
                          type="button"
                          onClick={() => toggleGroup(base)}
                          className="p-2 hover:bg-gray-200 rounded transition"
                          aria-label={isExpanded ? "Collapse group" : "Expand group"}
                        >
                          <ChevronDown
                            className={`w-5 h-5 text-gray-600 transition-transform ${
                              isExpanded ? "rotate-0" : "-rotate-90"
                            }`}
                          />
                        </button>
                      </div>
                    </div>

                    {isExpanded && (
                      <>
                        <div className="border-t border-gray-200 pt-3" />

                        <div className="space-y-3 sm:space-y-4">
                          {items.map((draft) => (
                            <div
                              key={draft.id}
                              className="bg-white border rounded-md overflow-hidden hover:shadow-md transition-all duration-200 border-gray-100"
                            >
                              <div className="flex items-center justify-between p-3 sm:p-4 border-b border-gray-100 gap-3">
                                <div className="flex items-center gap-3 min-w-0">
                                  {renderPlatformIcon(draft.platform)}
                                  <div className="min-w-0">
                                    <div className="font-semibold text-slate-900 capitalize truncate">
                                      {draft.platform}
                                    </div>
                                    <p className="text-sm text-gray-500 font-medium flex items-center gap-1">
                                      <Clock className="w-3 h-3 shrink-0" />
                                      <span>{formatTimeAgo(draft.updatedAt)}</span>
                                    </p>
                                  </div>
                                </div>
                                <button
                                  disabled={deletingId === draft.id}
                                  onClick={() => deleteHistory(draft?.id)}
                                  className="rounded-full disabled:cursor-not-allowed disabled:text-red-300 text-red-500 hover:text-red-600 transition-colors"
                                  title="Delete post"
                                >
                                  {deletingId === draft.id ? (
                                    <Loader className="animate-spin w-5 h-5" />
                                  ) : (
                                    <Trash2 className="w-4 h-4" />
                                  )}
                                </button>
                              </div>

                              <div className="flex flex-col sm:flex-row hover:bg-gray-50 transition-colors">
                                {renderThumbnail(draft)}

                                <div className="flex-1 p-3 sm:p-4 min-w-0">
                                  <p className="text-gray-500 font-medium text-sm line-clamp-3 mb-2 break-words">
                                    {draft.content}
                                  </p>

                                  <div className="flex items-center gap-2 flex-wrap">
                                    <p className="text-xs text-gray-500 font-medium">
                                      {format(new Date(draft.updatedAt), "PPP p")}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </InfiniteScroll>
      </div>

      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 p-4 bg-blue-600 text-white rounded-full shadow-xl hover:bg-blue-700 transition-all hover:scale-110 z-50"
          aria-label="Scroll to top"
        >
          <ChevronUp className="w-6 h-6" />
        </button>
      )}
    </div>
  );
};

export default DraftsPage;